import React, { useMemo } from 'react';
import noop from 'lodash/noop';
import { Box, xcss } from '@atlaskit/primitives';
import { getBaseAutomationUrl } from '@atlassian/jira-automation-platform/src/common/utils.tsx';
import { ManualRulesContainer } from '@atlassian/jira-automation-platform/src/ui/manual-rules-container/index.tsx';
import {
	useIssueId,
	useIssueKey,
	useProjectKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import {
	useIsSimplifiedProject,
	useProjectId,
	useProjectType,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { useCanAdministerProject } from '@atlassian/jira-project-permissions-service/src/main.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { getLoopedTransitionsForStatus } from '../../../common/utils.tsx';
import { StatusService } from '../../../services/status-service/index.tsx';
import { TransitionsQuery } from '../../../services/transitions-query/index.tsx';
import { ActionsMenu } from './actions-menu/index.tsx';
import type { Props, ActionsMenuComponentProps } from './types.tsx';

const ActionsMenuComponent = ({
	issueKey,
	issueId,
	environment,
	cloudId,
	projectType,
	transitions,
	onTransitionStatus,
	baseAutomationUrl,
	canManageAutomations,
}: ActionsMenuComponentProps) => (
	<Box xcss={actionsWrapperStyles}>
		<ManualRulesContainer
			env={environment}
			cloudId={cloudId}
			issueIds={issueId ? [Number(issueId)] : []}
		>
			{({ triggerFetch, initialised, rules, invokingRuleId, invokeRuleOrShowDialog }) => (
				<ActionsMenu
					issueKey={issueKey}
					issueId={Number(issueId)}
					projectType={projectType}
					rules={rules}
					triggerFetch={triggerFetch}
					executeRule={invokeRuleOrShowDialog}
					executingRule={invokingRuleId}
					initialised={initialised}
					transitions={transitions}
					onSelect={onTransitionStatus}
					canManageAutomations={canManageAutomations}
					baseAutomationUrl={baseAutomationUrl}
				/>
			)}
		</ManualRulesContainer>
	</Box>
);

const ActionsNew = ({ initialStatusValue, onSubmit, onSuccess }: Props) => {
	const issueKey = useIssueKey();
	const issueId = useIssueId();
	const baseUrl = '';
	const projectKey = useProjectKey();
	const projectId = useProjectId(projectKey);
	const isSimplified = useIsSimplifiedProject(projectKey);
	const projectType = useProjectType(projectKey);

	const baseAutomationUrl = useMemo(
		() => getBaseAutomationUrl({ projectKey, projectType, isSimplified }),
		[projectKey, projectType, isSimplified],
	);

	const isProjectAdmin = useCanAdministerProject(projectKey);
	const isGlobalAdmin = useIsAdmin();

	const canManageAutomations = isProjectAdmin || isGlobalAdmin;

	const { environment, cloudId } = useTenantContext();

	return (
		<StatusService
			appearance="button"
			projectType={projectType || null}
			issueKey={issueKey}
			issueId={issueId}
			baseUrl={baseUrl}
			onSubmit={onSubmit}
			onSuccess={onSuccess}
			onFailure={noop}
			onEditStart={noop}
			onEditCancel={noop}
			initialValue={initialStatusValue}
		>
			{({ value: statusServiceValue, loading: saving, transitionStatus }) => (
				<TransitionsQuery
					issueKey={issueKey}
					baseUrl={baseUrl}
					status={statusServiceValue}
					isSaving={saving}
					preFetch
					projectType={projectType}
				>
					{({ data: transitions }) => {
						const loopedTransitions = getLoopedTransitionsForStatus(
							transitions,
							statusServiceValue.id,
						);

						const menuProps: ActionsMenuComponentProps = {
							environment,
							cloudId,
							projectId,
							projectType,
							issueKey,
							issueId,
							transitions: loopedTransitions,
							onTransitionStatus: transitionStatus,
							baseAutomationUrl,
							canManageAutomations,
						};
						return <ActionsMenuComponent {...menuProps} />;
					}}
				</TransitionsQuery>
			)}
		</StatusService>
	);
};

const actionsWrapperStyles = xcss({
	display: 'inline-flex',
	maxWidth: '100%',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

export default ActionsNew;
